import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { getDaysInMonth } from '../hooks/useExpenseFilters';

const selectStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 0,
        backgroundColor: '#0f0f23',
        '& fieldset': {
            borderWidth: '2px',
            borderColor: '#374151',
        },
        '&:hover fieldset': {
            borderWidth: '2px',
            borderColor: '#6366f1',
        },
        '&.Mui-focused fieldset': {
            borderWidth: '3px',
            borderColor: '#6366f1',
        },
    },
    '& .MuiInputLabel-root': {
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '0.75rem',
        letterSpacing: '0.05em',
    },
};

const MONTHS = [
    { value: 1, label: 'Jan' },
    { value: 2, label: 'Feb' },
    { value: 3, label: 'Mar' },
    { value: 4, label: 'Apr' },
    { value: 5, label: 'May' },
    { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' },
    { value: 8, label: 'Aug' },
    { value: 9, label: 'Sep' },
    { value: 10, label: 'Oct' },
    { value: 11, label: 'Nov' },
    { value: 12, label: 'Dec' },
];

interface DateRangeFilterProps {
    label: string;
    year?: number;
    month?: number;
    day?: number;
    onYearChange: (year: number | undefined) => void;
    onMonthChange: (month: number | undefined) => void;
    onDayChange: (day: number | undefined) => void;
    startYear?: number;
    endYear?: number;
}

const DateRangeFilter = ({
    label,
    year,
    month,
    day,
    onYearChange,
    onMonthChange,
    onDayChange,
    startYear = 2020,
    endYear = new Date().getFullYear() + 1,
}: DateRangeFilterProps) => {
    const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);

    const daysInMonth = year && month ? getDaysInMonth(year, month) : 31;
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Typography
                sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.05em',
                    color: 'text.secondary',
                    minWidth: 40,
                }}
            >
                {label}
            </Typography>

            <FormControl size="small" sx={{ ...selectStyles, minWidth: 90 }}>
                <InputLabel id={`${label}-year-label`}>Year</InputLabel>
                <Select
                    labelId={`${label}-year-label`}
                    value={year ?? ''}
                    onChange={(e) => {
                        const value = e.target.value as number | '';
                        onYearChange(value === '' ? undefined : value);
                    }}
                    label="Year"
                >
                    <MenuItem value="">Any</MenuItem>
                    {years.map((y) => (
                        <MenuItem key={y} value={y}>
                            {y}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl size="small" sx={{ ...selectStyles, minWidth: 80 }}>
                <InputLabel id={`${label}-month-label`}>Month</InputLabel>
                <Select
                    labelId={`${label}-month-label`}
                    value={month ?? ''}
                    onChange={(e) => {
                        const value = e.target.value as number | '';
                        onMonthChange(value === '' ? undefined : value);
                    }}
                    label="Month"
                >
                    <MenuItem value="">Any</MenuItem>
                    {MONTHS.map((m) => (
                        <MenuItem key={m.value} value={m.value}>
                            {m.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl size="small" sx={{ ...selectStyles, minWidth: 70 }}>
                <InputLabel id={`${label}-day-label`}>Day</InputLabel>
                <Select
                    labelId={`${label}-day-label`}
                    value={day ?? ''}
                    onChange={(e) => {
                        const value = e.target.value as number | '';
                        onDayChange(value === '' ? undefined : value);
                    }}
                    label="Day"
                    disabled={!month}
                >
                    <MenuItem value="">Any</MenuItem>
                    {days.map((d) => (
                        <MenuItem key={d} value={d}>
                            {d}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Stack>
    );
};

export default DateRangeFilter;
